import Table from '@eduzz/houston-ui/Table';
import Typography from '@eduzz/houston-ui/Typography';
import moment from 'moment';
import React, { memo, useCallback, useEffect } from 'react';
import { FormattedNumber } from 'react-intl';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';
import InvoiceTags, { getIfAtLeastOneTagIsTrue } from 'v2/components/Shared/InvoiceTags';
import PaymentTypeImg from 'v2/components/Shared/PaymentTypeImg';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { Space } from 'v2/components/Shared/Space';
import { TableActionButton } from 'v2/components/Shared/TableActionButton';
import Truncate from 'v2/components/Shared/TruncateText';
import salesService from 'v2/services/sales/general';

import type { IInvoice, IProduct } from 'v2/interfaces/invoice';

import useStyles from './styles';

const headerDate = {
  created_at: 'Criação',
  refunded_at: 'Reembolso',
  received_at: 'Pagamento',
  clicked_at: 'Clique',
  credited_at: 'Crédito',
  expiration_at: 'Vencimento',
};

export interface IListProps {
  handleSetCurrentInvoice: (invoice: IInvoice) => void;
}

export const NewSalesList = memo(({ handleSetCurrentInvoice }: IListProps) => {
  const classes = useStyles();

  const [, , hasInvoicesError] = useObservable(() => salesService.load(), []);
  const [isLoading] = useObservable(() => salesService.isLoading(), []);
  const [invoices] = useObservable(() => salesService.getInvoices(), []);
  const [pagination] = useObservable(() => salesService.getPaginationResponse(), []);
  const [dateRef] = useObservable(() => salesService.getDateRef(), []);
  const [isTagsVisible] = useObservable(() => salesService.getTagsVisible(), []);

  useEffect(() => {
    if (!isLoading && hasInvoicesError) {
      toast.error('Algo de errado aconteceu, tente novamente.');
    }
  }, [isLoading, hasInvoicesError]);

  const onChangePerPage = useCallback(
    (per_page: number) => {
      salesService.setPagination({ ...pagination, per_page, current_page: 1 });
      document.getElementById('sales_table').scrollIntoView();
    },
    [pagination]
  );

  const onChangePage = useCallback(
    (page: number) => {
      salesService.setPagination({ ...pagination, current_page: page });
      document.getElementById('sales_table').scrollIntoView();
    },
    [pagination]
  );

  const renderProductTitle = (product: IProduct) => {
    if (!product) return null;
    const formattedProductTitle = formatProductTitle(product.title || '');
    if (product.title?.length < 40) {
      return (
        <Typography size='xxs' weight='semibold'>
          {formattedProductTitle}
        </Typography>
      );
    }
    return (
      <Typography size='xxs' weight='semibold'>
        <Truncate count={40} final='...'>
          {formattedProductTitle}
        </Truncate>
      </Typography>
    );
  };

  const formatProductTitle = (title: string) => {
    return title.replace(/^\(\d+\)\s/gi, '') || '';
  };

  function shouldShowTags(invoice: IInvoice) {
    return isTagsVisible && getIfAtLeastOneTagIsTrue(invoice.tags);
  }

  return (
    <Table id='sales_table' total={pagination?.total_items || 0} data-testid='new-sales-table' loading={isLoading}>
      <Table.Header>
        <Table.Column>{headerDate[dateRef]}</Table.Column>
        <Table.Column>Fatura</Table.Column>
        <Table.Column>Cliente</Table.Column>
        <Table.Column>Produto</Table.Column>
        <Table.Column>Afiliado</Table.Column>
        <Table.Column>UTM</Table.Column>
        <Table.Column align='right'>Ganho</Table.Column>
        <Table.Column></Table.Column>
      </Table.Header>
      <Table.Body>
        {invoices?.map((invoice, index) => (
          <>
            <Table.Row
              className={shouldShowTags(invoice) && classes.rowWithTags}
              key={invoice.id}
              data={invoice}
              index={index}
              data-testid='new-sales-table-row'
            >
              <Table.Cell>
                <Typography size='xxs'>{invoice[dateRef] ? moment(invoice[dateRef]).format('DD/MM/YYYY') : '-'}</Typography>
                <Typography size='xxs'>{invoice[dateRef] ? moment(invoice[dateRef]).format('HH:mm') : '-'}</Typography>
              </Table.Cell>
              <Table.Cell>
                <div className={`${classes.statusCell} ${invoice?.payment_status?.alias || ''}`}>
                  <Typography color='inherit' size='xxs' weight='semibold'>
                    {invoice.invoice_id}
                  </Typography>
                  <Typography className={classes.status} color='inherit' size='xxxs' weight='bold'>
                    {invoice?.payment_status?.name || ''}
                  </Typography>
                </div>
              </Table.Cell>
              <Table.Cell>
                <div>
                  <Typography size='xxs' weight='semibold'>
                    <Truncate count={35} final='...'>
                      {invoice?.customer?.name || ''}
                    </Truncate>
                  </Typography>
                  <Typography size='xxs'>
                    <Truncate count={40} final='...'>
                      {invoice?.customer?.email || ''}
                    </Truncate>
                  </Typography>
                </div>
              </Table.Cell>
              <Table.Cell>
                <div>
                  {renderProductTitle(invoice.product)}
                  <Typography size='xxs'>({invoice?.product?.id || ''})</Typography>
                </div>
              </Table.Cell>
              <Table.Cell>
                {invoice?.affiliate?.name && (
                  <>
                    <Typography size='xxs' weight='semibold'>
                      <Truncate count={35} final='...'>
                        {invoice?.affiliate?.name || ''}
                      </Truncate>
                    </Typography>
                    <Typography size='xxs'>
                      <Truncate count={40} final='...'>
                        {invoice?.affiliate?.email || ''}
                      </Truncate>
                    </Typography>
                  </>
                )}
                {!invoice?.affiliate?.name && <span>-</span>}
              </Table.Cell>
              <Table.Cell>
                <Typography size='xxs'>
                  <Truncate count={30} final='...'>
                    {invoice?.cart?.utm_source || '-'}
                  </Truncate>
                </Typography>
              </Table.Cell>
              <Table.Cell align='right'>
                <Typography className={classes.gainCell} weight='semibold'>
                  <>
                    <PermissionHide inverse action='myeduzz:minhas-vendas:ocultar-valores' fallback={<em>oculto</em>}>
                      <FormattedNumber value={invoice?.net_income || 0} format={invoice?.currency?.code || ''} />
                    </PermissionHide>
                    <Space axis='horizontal' size={16} />
                    <PaymentTypeImg invoice={invoice} />
                  </>
                </Typography>
              </Table.Cell>
              <Table.Cell align='right'>
                <TableActionButton onClick={() => handleSetCurrentInvoice(invoice)} />
              </Table.Cell>
            </Table.Row>
            {shouldShowTags(invoice) && (
              <Table.Row className={classes.tagsRow} data={invoice} index={index}>
                <Table.Cell className={classes.tagsCell} colSpan={8}>
                  <InvoiceTags size='small' invoice={invoice} spacing={1} />
                </Table.Cell>
              </Table.Row>
            )}
          </>
        ))}
      </Table.Body>

      <Table.Pagination
        page={pagination?.current_page}
        perPage={pagination?.per_page}
        onChangePage={onChangePage}
        onChangePerPage={onChangePerPage}
        optionsPerPage='5,10,25,50'
      />
    </Table>
  );
});
